// Tipos da produção (INDUSTRIA.md, onda 2): ficha técnica versionada, ordem de
// produção, apontamento lote a lote e as duas consultas de rastreabilidade.
import type { MaterialKind, MaterialUnit } from './industria'

export type RecipeStatus = 'draft' | 'active' | 'obsolete'
export type OrderStatus =
  | 'planned' | 'released' | 'in_progress' | 'reported'
  | 'pending_approval' | 'closed' | 'cancelled'
export type ConsumptionKind = 'consumo' | 'perda'
export type RequestStatus = 'requested' | 'accepted' | 'scheduled' | 'rejected' | 'converted'
export type ApprovalStatus = 'pending' | 'approved' | 'rejected'

export const RECIPE_STATUS_LABEL: Record<RecipeStatus, string> = {
  draft: 'Rascunho', active: 'Ativa', obsolete: 'Obsoleta',
}

export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  planned: 'Planejada',
  released: 'Liberada',
  in_progress: 'Em produção',
  reported: 'Apontada',
  pending_approval: 'Aguardando aprovação',
  closed: 'Encerrada',
  cancelled: 'Cancelada',
}

export const ORDER_STATUS_BADGE: Record<OrderStatus, 'default' | 'success' | 'warning' | 'danger' | 'info'> = {
  planned: 'default',
  released: 'info',
  in_progress: 'info',
  reported: 'warning',
  pending_approval: 'danger',
  closed: 'success',
  cancelled: 'default',
}

export const APPROVAL_STATUS_LABEL: Record<ApprovalStatus, string> = {
  pending: 'Pendente', approved: 'Aprovada', rejected: 'Reprovada',
}

export const REQUEST_STATUS_LABEL: Record<RequestStatus, string> = {
  requested: 'Aguardando resposta',
  accepted: 'Aceita',
  scheduled: 'Programada',
  rejected: 'Recusada',
  converted: 'Virou ordem',
}

export const REQUEST_STATUS_BADGE: Record<RequestStatus, 'default' | 'success' | 'warning' | 'danger' | 'info'> = {
  requested: 'warning', accepted: 'info', scheduled: 'info', rejected: 'danger', converted: 'success',
}

export type RecipeRow = {
  id: string; tenantId: string; tenantName: string | null
  productId: string; productSku: string | null; productName: string | null
  version: number; status: RecipeStatus; validFrom: string | null
  notes: string | null; createdAt: string; itemCount: number
}

export type RecipeItem = {
  id: string; materialId: string; materialCode: string; materialName: string
  unit: MaterialUnit; kind: MaterialKind; lotControlled: boolean
  ownerTenantId: string | null
  qtyPerUnit: string; scrapPct: string; scrapLimitPct: string
  required: boolean; sortOrder: number
}

export type RecipeDetail = Omit<RecipeRow, 'itemCount'> & { items: RecipeItem[] }

export type OrderRow = {
  id: string; number: string; tenantId: string; tenantName: string | null
  productId: string; productSku: string | null; productName: string | null
  recipeVersion: number | null
  qtyPlanned: number; qtyProduced: number
  lotNumber: string | null; expiryDate: string | null
  status: OrderStatus; scheduledFor: string | null
  createdAt: string; closedAt: string | null
  pendingApprovals: number
}

export type OrderItem = RecipeItem & {
  need: number; consumed: number; scrapped: number
  scrapLimit: number; overScrapLimit: boolean; remaining: number; reserved: number
}

export type Consumption = {
  id: string; materialId: string; materialCode: string; materialName: string
  unit: MaterialUnit; lotId: string | null; lotNumber: string | null
  volumeCode: string | null; quantity: number; kind: ConsumptionKind
  reason: string | null; author: string | null; createdAt: string
}

export type Approval = {
  id: string; materialId: string; materialCode: string; materialName: string
  qtyScrap: string; qtyLimit: string; status: ApprovalStatus
  reason: string | null; decidedAt: string | null; createdAt: string
}

export type ApprovalRow = Approval & {
  orderId: string; orderNumber: string
  tenantName: string | null; productSku: string | null
}

export type OrderDetail = Omit<OrderRow, 'pendingApprovals'> & {
  recipeId: string
  releasedAt: string | null; startedAt: string | null; notes: string | null
  // Onda 4: a OP encerrada vira entrada no estoque do WsFull.
  stockEntryAt: string | null; stockEntryLocation: string | null; requestId: string | null
  items: OrderItem[]
  consumptions: Consumption[]
  approvals: Approval[]
  reservations: { materialId: string; qtyReserved: string; qtyConsumed: string; released: boolean }[]
}

export type TraceComponent = {
  materialCode: string; materialName: string; unit: MaterialUnit
  kind: ConsumptionKind; quantity: number
  lotId: string | null; lotNumber: string | null; supplierLot: string | null
  supplierName: string | null; docNumber: string | null; docKey: string | null
  entryMode: string | null; expiryDate: string | null; receivedAt: string | null
}

export type TraceBackwardRow = {
  id: string; number: string; lotNumber: string | null
  productSku: string | null; productName: string | null
  tenantId: string; tenantName: string | null
  qtyProduced: number; expiryDate: string | null
  status: OrderStatus; closedAt: string | null
  components: TraceComponent[]
}

export type TraceForwardRow = {
  id: string; lotNumber: string; supplierLot: string | null; supplierName: string | null
  docNumber: string | null; entryMode: string; receivedAt: string; qtyReceived: number
  materialCode: string; materialName: string; unit: MaterialUnit
  ownerName: string | null; remaining: number
  orders: {
    orderId: string; orderNumber: string; productLot: string | null
    productSku: string | null; productName: string | null; tenantName: string | null
    status: OrderStatus; qtyProduced: number; closedAt: string | null; quantity: number
  }[]
  volumes: { id: string; code: string; qtyCurrent: number; status: string; locationCode: string | null }[]
}

export type RequestRow = {
  id: string; tenantId: string; tenantName: string | null
  productId: string; productSku: string | null; productName: string | null
  qty: number; desiredDate: string | null; status: RequestStatus
  notes: string | null; decisionNotes: string | null
  productionOrderId: string | null; orderNumber: string | null
  decidedAt: string | null; createdAt: string; author: string | null
  hasActiveRecipe: boolean
}

/** Painel do portal da marca. */
export type MyProduction = {
  orders: OrderRow[]
  requests: RequestRow[]
  produced: {
    productId: string; productSku: string | null; productName: string | null
    qty: number; lastAt: string | null
  }[]
}

/** Quantidade por unidade é fracionária (a caixa master entra como 1/24) —
 *  mostrar 0,041667 assusta; mostrar "1 a cada 24" explica. */
export function fmtQtyPerUnit(v: string | number): string {
  const n = Number(v)
  if (!n) return '0'
  if (n >= 1) return n.toLocaleString('pt-BR', { maximumFractionDigits: 4 })
  const each = Math.round(1 / n)
  return `${n.toLocaleString('pt-BR', { maximumFractionDigits: 6 })} (1 a cada ${each})`
}
